import React, { useEffect, useRef, useState } from "react";

import TuneIcon from "@mui/icons-material/Tune";
import { Divider, FormControlLabel, IconButton, Popover, Stack, Switch } from "@mui/material";
import { Menu } from "components/popups/Menu/Menu";
import HighchartsReact from "highcharts-react-official";
import { ReportCashflowAum, UpdateCashflowAum } from "store/types";

import { PopoverContent } from "./AumTotalContent";

interface IProps {
  aumData: ReportCashflowAum | null;
  currentSetId: number | null;
  chartRef: React.RefObject<HighchartsReact.RefObject>;
  updateAumData: (data: UpdateCashflowAum) => void;
  loadingStatus: {
    aumLoading: boolean;
    allocationTargetLoading: boolean;
    graphLoading: boolean;
  };
}

export const ChartSettings: React.FC<IProps> = ({ aumData, currentSetId, chartRef, updateAumData, loadingStatus }) => {
  const menuRef = useRef<HTMLButtonElement>(null);
  const switchRef = useRef<HTMLButtonElement>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLButtonElement | null>(null);
  // aumEnabled state, will be saved in db only after click apply button
  const [aumEnabled, setAumEnabled] = useState(false);

  const handleExport = (type: string) => {
    (chartRef.current?.chart as any)?.exportChartLocal({ type });
  };

  const updateAumSettings = async (newTotalAum: number | null) => {
    if (!currentSetId) return;

    await updateAumData({
      set_id: currentSetId,
      aum_enabled: aumEnabled,
      total_aum: newTotalAum ?? aumData?.total_aum ?? 0
    });
    setMenuAnchorEl(null);
  };

  const onSwitchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setAumEnabled(e.target.checked);
    if (!aumEnabled) {
      //if aum is disabled, open menu for set total aum
      setMenuAnchorEl(switchRef.current);
    } else {
      // if aum is enabled, disable it and save to db
      currentSetId &&
        (await updateAumData({
          set_id: currentSetId,
          aum_enabled: false,
          total_aum: aumData?.total_aum ?? 0
        }));
    }
  };

  const onClose = () => {
    //reset aumEnabled state, to be like in db
    setAumEnabled(aumData?.aum_enabled || false);
    setMenuAnchorEl(null);
  };

  const menuOptions = [
    {
      title: "Download PNG Image",
      onClick: () => handleExport("image/png")
    },
    {
      title: "Download JPEG Image",
      onClick: () => handleExport("image/jpeg")
    },
    {
      title: "Download PDF Document",
      onClick: () => handleExport("application/pdf")
    },
    {
      title: "Download SVG Vector Image",
      onClick: () => handleExport("image/svg+xml")
    }
  ];

  useEffect(() => {
    if (!aumData) return;
    setAumEnabled(aumData?.aum_enabled || false);
  }, [aumData]);

  return (
    <>
      <Stack sx={{ flexDirection: "row", gap: "10px", alignItems: "center", height: 24 }}>
        <FormControlLabel
          sx={{
            m: 0,
            gap: "10px",

            ".MuiTypography-root": {
              fontSize: "15px",
              lineHeight: "20px",
              color: "#071F35",
              fontWeight: 400,
              whiteSpace: "nowrap"
            }
          }}
          labelPlacement="start"
          control={
            <Switch
              ref={switchRef}
              disabled={Object.values(loadingStatus).some((status) => status === true)}
              checked={aumEnabled}
              onChange={onSwitchChange}
            />
          }
          label="AUM Indication"
        />
        {aumData?.aum_enabled && (
          <>
            <Divider orientation="vertical" flexItem sx={{ height: "100%", borderColor: "#DBE8F5" }} />
            <IconButton
              disabled={Object.values(loadingStatus).some((status) => status === true)}
              onClick={(e) => setMenuAnchorEl(e.currentTarget)}
            >
              <TuneIcon sx={{ height: 20, width: 20 }} />
            </IconButton>
          </>
        )}

        <Popover
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={onClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right"
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right"
          }}
        >
          <PopoverContent onClose={onClose} aumData={aumData} updateAumSettings={updateAumSettings} />
        </Popover>

        <Divider orientation="vertical" flexItem sx={{ height: "100%", borderColor: "#DBE8F5" }} />

        <Menu
          iconButtonProps={{
            className: "menu-trigger",
            ref: menuRef
          }}
          keepTriggerEnabled
          options={menuOptions}
        />
      </Stack>
    </>
  );
};
